/**
 * Agent stream bridge — converts `agent.thought` events from the unified
 * WebSocket `agents` channel into Office Agent-Chat messages.
 *
 * The backend publishes agent reasoning on the `agents` channel
 * (`event_publisher.publish_agent_thought` → type "agent.thought"); this is
 * the frontend consumer that turns those events into `AccumulatedMessage`
 * entries for the Trading Floor feed.
 */

import { AGENTS_CONFIG } from "../config/agents.config";
import type { AccumulatedMessage } from "../store/flow.store";

function rosterMetaFor(agentName: string) {
  // Backend keys use underscores (ben_kim); roster keys use dashes (ben-kim).
  const normalized = agentName.trim().toLowerCase().replace(/_/g, "-");
  for (const meta of Object.values(AGENTS_CONFIG)) {
    if (
      meta.name.toLowerCase() === normalized ||
      meta.shortName.toLowerCase() === normalized ||
      meta.key.toLowerCase() === normalized
    ) {
      return meta;
    }
  }
  return null;
}

/** Resolve a roster avatar for an agent display name (DL-48 canon). */
export function avatarForAgentName(agentName: string): string {
  return rosterMetaFor(agentName)?.avatarUrl ?? "";
}

/** Resolve a roster display name for a backend agent key/name. */
export function displayNameForAgent(agentName: string): string {
  return rosterMetaFor(agentName)?.name ?? agentName;
}

function messageTypeFor(eventType: unknown): AccumulatedMessage["type"] {
  switch (String(eventType || "").toLowerCase()) {
    case "handoff":
      return "handoff";
    case "decision":
    case "action":
      return "decision";
    default:
      return "message";
  }
}

interface AgentThoughtOptions {
  /**
   * Run ids the desk is currently displaying (live run + selected/loaded run).
   * An `agent.thought` is only surfaced when its `run_id` is in this set — the
   * ownership marker that ties a thought to the run the user is looking at.
   */
  allowedRunIds: readonly (string | null | undefined)[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredMatchingAlias(
  payload: Record<string, unknown>,
  primary: string,
  alias?: string,
  primaryRequired = true,
): string | null {
  if (
    primaryRequired &&
    (payload[primary] === undefined || payload[primary] === null)
  ) {
    return null;
  }
  const values: string[] = [];
  for (const key of alias ? [primary, alias] : [primary]) {
    const value = payload[key];
    if (value === undefined || value === null) continue;
    if (typeof value !== "string" || !value.trim()) return null;
    values.push(value.trim());
  }
  if (values.length === 0 || values.some((value) => value !== values[0])) {
    return null;
  }
  return values[0];
}

function optionalString(
  payload: Record<string, unknown>,
  key: string,
): { valid: boolean; value: string | undefined } {
  const rawValue = payload[key];
  if (rawValue === undefined || rawValue === null) {
    return { valid: true, value: undefined };
  }
  if (typeof rawValue !== "string" || !rawValue.trim()) {
    return { valid: false, value: undefined };
  }
  return { valid: true, value: rawValue.trim() };
}

function optionalConfidence(
  payload: Record<string, unknown>,
  metadata: Record<string, unknown>,
): { valid: boolean; value: number | undefined } {
  const candidates = [metadata.confidence, payload.confidence].filter(
    (value) => value !== undefined && value !== null,
  );
  if (
    candidates.some(
      (value) => typeof value !== "number" || !Number.isFinite(value),
    )
  ) {
    return { valid: false, value: undefined };
  }
  const values = candidates as number[];
  if (values.length > 1 && values.some((value) => value !== values[0])) {
    return { valid: false, value: undefined };
  }
  return { valid: true, value: values[0] };
}

interface TranscriptMessageFields {
  expectedRunId: string;
  runId: unknown;
  eventId: unknown;
  agent: unknown;
  content: unknown;
  timestamp: unknown;
}

export function validateTranscriptMessageFields({
  expectedRunId,
  runId,
  eventId,
  agent,
  content,
  timestamp,
}: TranscriptMessageFields): {
  eventId: string;
  agent: string;
  content: string;
  timestamp: Date;
} | null {
  const normalizedRunId = typeof runId === "string" ? runId.trim() : "";
  const normalizedEventId = typeof eventId === "string" ? eventId.trim() : "";
  const normalizedAgent = typeof agent === "string" ? agent.trim() : "";
  const normalizedContent = typeof content === "string" ? content.trim() : "";
  const normalizedTimestamp =
    typeof timestamp === "string" ? timestamp.trim() : "";
  const parsedTimestamp = new Date(normalizedTimestamp);
  if (
    normalizedRunId !== expectedRunId ||
    !normalizedEventId ||
    !normalizedAgent ||
    !normalizedContent ||
    !normalizedTimestamp ||
    Number.isNaN(parsedTimestamp.getTime())
  ) {
    return null;
  }
  return {
    eventId: normalizedEventId,
    agent: normalizedAgent,
    content: normalizedContent,
    timestamp: parsedTimestamp,
  };
}

/**
 * Convert an `agent.thought` websocket envelope into an AccumulatedMessage.
 *
 * Ownership guard (fail-closed): the event MUST carry a `run_id` that matches
 * one of the desk's active run ids. The unified socket is per-user
 * authenticated, but a single user can have runs across profiles — an event
 * for a run the desk is NOT displaying (other profile / stale run) must not
 * leak into this chat. An event with NO `run_id` marker is DROPPED (backend
 * gap: `publish_agent_thought` does not always stamp run_id/profile — until it
 * does, unattributable thoughts cannot be safely attributed here).
 *
 * Returns null for anything that is not a well-formed, owned agent thought —
 * the caller must not fabricate chat entries from partial payloads.
 */
export function agentThoughtToMessage(
  data: unknown,
  options: AgentThoughtOptions,
): AccumulatedMessage | null {
  if (!isRecord(data) || data.type !== "agent.thought") return null;
  if (!isRecord(data.payload)) return null;

  // Ownership: drop events without a run_id (fail closed) or whose run_id is
  // not one the desk is currently displaying.
  const runId =
    typeof data.payload.run_id === "string" ? data.payload.run_id.trim() : "";
  const allowed = new Set(
    options.allowedRunIds
      .filter(
        (id): id is string => typeof id === "string" && id.trim().length > 0,
      )
      .map((id) => id.trim()),
  );
  if (!runId || !allowed.has(runId)) return null;
  return canonicalTranscriptPayloadToMessage(data.payload, runId);
}

/**
 * Normalize one canonical transcript occurrence regardless of transport.
 * Payload aliases are accepted only when they agree; identity, ownership, and
 * occurrence time are never synthesized from the WebSocket envelope.
 */
export function canonicalTranscriptPayloadToMessage(
  payload: unknown,
  expectedRunId: string,
): AccumulatedMessage | null {
  if (!isRecord(payload)) return null;

  const eventId = requiredMatchingAlias(payload, "event_id", "id");
  const sourceAgent = requiredMatchingAlias(
    payload,
    "from_agent",
    "agent_name",
  );
  const content = requiredMatchingAlias(payload, "content", "thought");
  const occurrenceTime = requiredMatchingAlias(
    payload,
    "created_at",
    "timestamp",
    false,
  );
  const eventType = requiredMatchingAlias(
    payload,
    "event_type",
    "message_type",
  );
  const toAgent = optionalString(payload, "to_agent");
  const nodeName = optionalString(payload, "node_name");
  if (
    eventId === null ||
    sourceAgent === null ||
    content === null ||
    occurrenceTime === null ||
    eventType === null ||
    !toAgent.valid ||
    !nodeName.valid ||
    !isRecord(payload.metadata)
  ) {
    return null;
  }

  const normalizedExpectedRunId = expectedRunId.trim();
  if (!normalizedExpectedRunId) return null;
  const fields = validateTranscriptMessageFields({
    expectedRunId: normalizedExpectedRunId,
    runId: payload.run_id,
    eventId,
    agent: sourceAgent,
    content,
    timestamp: occurrenceTime,
  });
  if (!fields) return null;

  const confidence = optionalConfidence(payload, payload.metadata);
  if (!confidence.valid) return null;
  const metadata = {
    ...payload.metadata,
    confidence: confidence.value,
    toAgent: toAgent.value,
    nodeName: nodeName.value,
    eventType,
  };

  return {
    id: fields.eventId,
    agent: displayNameForAgent(fields.agent),
    agentAvatar: avatarForAgentName(fields.agent),
    content: fields.content,
    timestamp: fields.timestamp,
    type: messageTypeFor(eventType),
    metadata,
  };
}

/**
 * Convert a persisted transcript event (SV-18 run transcript) into an
 * Agent-Chat message. Returns null for events without agent + content.
 */
export function transcriptEventToMessage(
  event: unknown,
  expectedRunId: string,
): AccumulatedMessage | null {
  return canonicalTranscriptPayloadToMessage(event, expectedRunId);
}

export function transcriptEventsToMessages(
  events: readonly unknown[],
  expectedRunId: string,
): AccumulatedMessage[] {
  const seenIds = new Set<string>();
  const messages: AccumulatedMessage[] = [];
  for (const event of events) {
    const message = transcriptEventToMessage(event, expectedRunId);
    if (!message || seenIds.has(message.id)) continue;
    seenIds.add(message.id);
    messages.push(message);
  }
  return messages;
}
